import { createTheme, PaletteMode } from '@mui/material/styles';
import type { PaletteOptions } from '@mui/material/styles';
import { theme as customTheme } from '../styles/theme';

// Mode-specific palettes. The light palette is the original GreenShield design
// system; the dark palette keeps the same brand hues but flips surfaces/text.
// Anything not listed here (spacing, typography, shadows, component overrides)
// is shared across modes inside buildMuiTheme().
const palettes: Record<PaletteMode, PaletteOptions> = {
  light: {
    mode: 'light' as PaletteMode,
    primary: {
      main: '#0056d2', // GreenShield primary blue
      light: '#4d8eff',
      dark: '#003d96',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#6C757D',
      light: '#919ca6',
      dark: '#495057',
      contrastText: '#ffffff',
    },
    success: {
      main: '#198754',
      light: '#28a745',
      dark: '#146c43',
      contrastText: '#ffffff',
    },
    error: {
      main: '#DC3545',
      light: '#e74c5c',
      dark: '#b02a37',
      contrastText: '#ffffff',
    },
    warning: {
      main: '#FFC107',
      light: '#ffcd39',
      dark: '#e0a800',
      contrastText: '#212529',
    },
    info: {
      main: '#0DCAF0',
      light: '#3fd5f4',
      dark: '#0aa2c0',
      contrastText: '#212529',
    },
    grey: {
      50: '#F8F9FA',
      100: '#E9ECEF',
      200: '#DEE2E6',
      300: '#CED4DA',
      400: '#ADB5BD',
      500: '#6C757D',
      600: '#495057',
      700: '#343A40',
      800: '#212529',
      900: '#000000',
    },
    background: {
      default: '#f9fafb',
      paper: '#ffffff',
    },
    text: {
      primary: '#212529',
      secondary: '#495057',
      disabled: '#ADB5BD',
    },
  },
  dark: {
    mode: 'dark' as PaletteMode,
    primary: {
      // Brand blue lightened one step so it stays legible on dark surfaces.
      main: '#4d8eff',
      light: '#7fadff',
      dark: '#0056d2',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#919ca6',
      light: '#adb5bd',
      dark: '#6C757D',
      contrastText: '#ffffff',
    },
    success: {
      main: '#28a745',
      light: '#5cb85c',
      dark: '#198754',
      contrastText: '#ffffff',
    },
    error: {
      main: '#e74c5c',
      light: '#ef7583',
      dark: '#DC3545',
      contrastText: '#ffffff',
    },
    warning: {
      main: '#FFC107',
      light: '#ffcd39',
      dark: '#e0a800',
      contrastText: '#212529',
    },
    info: {
      main: '#3fd5f4',
      light: '#6ee0f7',
      dark: '#0aa2c0',
      contrastText: '#212529',
    },
    grey: {
      50: '#F8F9FA',
      100: '#E9ECEF',
      200: '#DEE2E6',
      300: '#CED4DA',
      400: '#ADB5BD',
      500: '#6C757D',
      600: '#495057',
      700: '#343A40',
      800: '#212529',
      900: '#000000',
    },
    background: {
      default: '#121417',
      paper: '#1c2025',
    },
    text: {
      primary: '#E9ECEF',
      secondary: '#ADB5BD',
      disabled: '#6C757D',
    },
  },
};

// Create a comprehensive MUI theme that integrates the custom design system.
// The palette varies by mode; everything else is shared.
export const buildMuiTheme = (mode: PaletteMode) => createTheme({
  palette: palettes[mode],
  typography: {
    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    h1: {
      fontSize: '2rem', // 32px
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: '-0.01562em',
    },
    h2: {
      fontSize: '1.5rem', // 24px
      fontWeight: 700,
      lineHeight: 1.3,
      letterSpacing: '-0.00833em',
    },
    h3: {
      fontSize: '1.25rem', // 20px
      fontWeight: 600,
      lineHeight: 1.4,
      letterSpacing: '0em',
    },
    h4: {
      fontSize: '1.125rem', // 18px
      fontWeight: 600,
      lineHeight: 1.5,
      letterSpacing: '0.00735em',
    },
    h5: {
      fontSize: '1rem', // 16px
      fontWeight: 600,
      lineHeight: 1.5,
      letterSpacing: '0em',
    },
    h6: {
      fontSize: '0.875rem', // 14px
      fontWeight: 600,
      lineHeight: 1.5,
      letterSpacing: '0.0075em',
    },
    body1: {
      fontSize: '1rem', // 16px
      fontWeight: 400,
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.875rem', // 14px
      fontWeight: 400,
      lineHeight: 1.5,
    },
    caption: {
      fontSize: '0.75rem', // 12px
      fontWeight: 400,
      lineHeight: 1.5,
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
    },
  },
  spacing: 8, // Base spacing unit (8px)
  shape: {
    borderRadius: 8, // Default border radius
  },
  shadows: [
    'none',
    '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    '0 2px 4px rgba(0, 0, 0, 0.1)',
    '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    '0 3px 5px -1px rgba(0, 0, 0, 0.08)',
    '0 4px 6px -2px rgba(0, 0, 0, 0.1)',
    '0 5px 8px -3px rgba(0, 0, 0, 0.12)',
    '0 6px 10px -4px rgba(0, 0, 0, 0.14)',
    '0 8px 12px -5px rgba(0, 0, 0, 0.16)',
    '0 10px 14px -6px rgba(0, 0, 0, 0.18)',
    '0 12px 16px -7px rgba(0, 0, 0, 0.2)',
    '0 14px 18px -8px rgba(0, 0, 0, 0.22)',
    '0 16px 20px -9px rgba(0, 0, 0, 0.24)',
    '0 18px 22px -10px rgba(0, 0, 0, 0.26)',
    '0 20px 24px -11px rgba(0, 0, 0, 0.28)',
    '0 22px 26px -12px rgba(0, 0, 0, 0.3)',
    '0 24px 28px -13px rgba(0, 0, 0, 0.32)',
    '0 26px 30px -14px rgba(0, 0, 0, 0.34)',
    '0 28px 32px -15px rgba(0, 0, 0, 0.36)',
    '0 30px 34px -16px rgba(0, 0, 0, 0.38)',
  ],
  transitions: {
    duration: {
      shortest: 150,
      shorter: 200,
      short: 250,
      standard: 300,
      complex: 375,
      enteringScreen: 225,
      leavingScreen: 195,
    },
  },
  components: {
    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          transition: 'none !important',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
        },
        elevation0: {
          boxShadow: 'none',
        },
        elevation1: {
          boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
        },
        elevation2: {
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
        },
        elevation3: {
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        },
      },
    },
    MuiTooltip: {
      defaultProps: {
        disableInteractive: true,
        enterDelay: 0,
        enterNextDelay: 0,
        leaveDelay: 0,
        TransitionProps: {
          timeout: 0,
        },
      },
      styleOverrides: {
        tooltip: {
          fontSize: '0.75rem',
          fontWeight: 500,
          backgroundColor: 'rgba(33, 37, 41, 0.95)',
        },
        popper: {
          '&[data-popper-placement*="bottom"] .MuiTooltip-tooltip': {
            marginTop: '4px',
          },
          '&[data-popper-placement*="top"] .MuiTooltip-tooltip': {
            marginBottom: '4px',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 600,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
          },
        },
        // Dark mode: full-brightness brand fills glare against dark surfaces
        // (Material dark guidance: dim + desaturate). Muted fills for contained
        // buttons; softened text/border tones for outlined & text variants.
        // Light mode is untouched — these keys exist only in the dark branch.
        ...(mode === 'dark' ? {
          containedPrimary: { backgroundColor: '#35619f', '&:hover': { backgroundColor: '#4071b8' } },
          containedError: { backgroundColor: '#a63b46', '&:hover': { backgroundColor: '#bd4753' } },
          containedSuccess: { backgroundColor: '#1e7d38', '&:hover': { backgroundColor: '#249343' } },
          containedWarning: { backgroundColor: '#8f7210', color: '#f5ecd2', '&:hover': { backgroundColor: '#a5850f' } },
          containedInfo: { backgroundColor: '#2b869c', color: '#e8f7fb', '&:hover': { backgroundColor: '#329bb4' } },
          outlinedPrimary: { color: '#6f9ce0', borderColor: 'rgba(111, 156, 224, 0.5)' },
          outlinedWarning: { color: '#d9b44a', borderColor: 'rgba(217, 180, 74, 0.5)' },
          outlinedInfo: { color: '#66bfd4', borderColor: 'rgba(102, 191, 212, 0.5)' },
          outlinedError: { color: '#d97781', borderColor: 'rgba(217, 119, 129, 0.5)' },
          outlinedSuccess: { color: '#5cb87a', borderColor: 'rgba(92, 184, 122, 0.5)' },
          textWarning: { color: '#d9b44a' },
          textInfo: { color: '#66bfd4' },
          textError: { color: '#d97781' },
          textSuccess: { color: '#5cb87a' },
        } : {}),
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 500,
        },
        // Dark mode: same muted-fill treatment as MuiButton for filled chips.
        ...(mode === 'dark' ? {
          colorPrimary: { '&.MuiChip-filled': { backgroundColor: '#35619f' } },
          colorError: { '&.MuiChip-filled': { backgroundColor: '#a63b46' } },
          colorSuccess: { '&.MuiChip-filled': { backgroundColor: '#1e7d38' } },
          colorWarning: { '&.MuiChip-filled': { backgroundColor: '#8f7210', color: '#f5ecd2' } },
          colorInfo: { '&.MuiChip-filled': { backgroundColor: '#2b869c', color: '#e8f7fb' } },
        } : {}),
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        size: 'small',
      },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: mode === 'light' ? '#E9ECEF' : 'rgba(255, 255, 255, 0.12)',
        },
      },
    },
    // MUI X DataGrid defaults its header strip to a light grey that does not
    // follow the dark palette — any un-styled DataGrid (e.g. report pages)
    // rendered a light header under light text in dark mode. Typed loosely
    // because the x-data-grid theme augmentation isn't imported here.
    ...(mode === 'dark' ? ({
      MuiDataGrid: {
        styleOverrides: {
          columnHeaders: { backgroundColor: '#1f242a' },
          columnHeader: { backgroundColor: '#1f242a' },
          footerContainer: { backgroundColor: '#1f242a' },
        },
      },
    } as Record<string, unknown>) : {}),
  },
});

// Default export stays the light theme so existing direct importers (specs,
// storybook-style usage) are unaffected. App.tsx uses useAppTheme() instead.
const muiTheme = buildMuiTheme('light');

// Export custom theme values for non-MUI usage
export const customColors = customTheme.colors;
export const urgencyLevels = {
  critical: {
    color: '#DC3545',
    backgroundColor: 'rgba(220, 53, 69, 0.1)',
    borderColor: '#DC3545',
    label: 'Critical',
    priority: 5,
  },
  urgent: {
    color: '#FD7E14',
    backgroundColor: 'rgba(253, 126, 20, 0.1)',
    borderColor: '#FD7E14',
    label: 'Urgent',
    priority: 4,
  },
  warning: {
    color: '#FFC107',
    backgroundColor: 'rgba(255, 193, 7, 0.1)',
    borderColor: '#FFC107',
    label: 'Warning',
    priority: 3,
  },
  attention: {
    color: '#0056d2',
    backgroundColor: 'rgba(0, 86, 210, 0.1)',
    borderColor: '#0056d2',
    label: 'Attention',
    priority: 2,
  },
  normal: {
    color: '#198754',
    backgroundColor: 'rgba(25, 135, 84, 0.1)',
    borderColor: '#198754',
    label: 'Normal',
    priority: 1,
  },
};

// Helper function to get urgency level configuration
export const getUrgencyConfig = (level: keyof typeof urgencyLevels) => {
  return urgencyLevels[level] || urgencyLevels.normal;
};

export type UrgencyLevel = keyof typeof urgencyLevels;

// ==================== NOTES SYSTEM COLORS ====================

/**
 * Note Source Colors - Visual indicators for note origin
 * Used by NoteSourceBadge component to distinguish between different note sources
 */
export const noteSourceColors = {
  patient: {
    color: '#1976d2', // Blue
    backgroundColor: 'rgba(25, 118, 210, 0.1)',
    borderColor: '#1976d2',
    label: 'Patient Note',
    icon: '👤', // Person icon for patient
    description: 'Patient-level notes visible across all orders and subscriptions',
  },
  order: {
    color: '#2e7d32', // Green
    backgroundColor: 'rgba(46, 125, 50, 0.1)',
    borderColor: '#2e7d32',
    label: 'Order Note',
    icon: '📋', // Clipboard icon for order
    description: 'Order-specific notes for this fulfillment workflow',
  },
  subscription: {
    color: '#ed6c02', // Orange
    backgroundColor: 'rgba(237, 108, 2, 0.1)',
    borderColor: '#ed6c02',
    label: 'Subscription Note',
    icon: '💊', // Pill icon for subscription
    description: 'Recurring subscription and refill scheduling notes',
  },
  billing: {
    color: '#9c27b0', // Purple
    backgroundColor: 'rgba(156, 39, 176, 0.1)',
    borderColor: '#9c27b0',
    label: 'Billing Note',
    icon: '🏥', // Hospital icon for billing
    description: 'Billing and insurance processing notes',
  },
};

/**
 * Note Type Colors - Visual indicators for note category
 * Used by NoteTypeIcon component to show the purpose of the note
 */
export const noteTypeColors = {
  internal: {
    color: '#495057', // Dark gray
    backgroundColor: 'rgba(73, 80, 87, 0.1)',
    borderColor: '#495057',
    label: 'Internal Note',
    icon: '📝', // Memo icon for internal notes
    description: 'Internal pharmacy staff workflow notes',
  },
  clinical: {
    color: '#0DCAF0', // Cyan (info color)
    backgroundColor: 'rgba(13, 202, 240, 0.1)',
    borderColor: '#0DCAF0',
    label: 'Clinical Note',
    icon: '⚕️', // Medical symbol for clinical
    description: 'Pharmacist clinical verification and review notes',
  },
  delivery: {
    color: '#FD7E14', // Orange
    backgroundColor: 'rgba(253, 126, 20, 0.1)',
    borderColor: '#FD7E14',
    label: 'Delivery Instructions',
    icon: '🚚', // Truck icon for delivery
    description: 'Driver-visible delivery instructions (gate codes, buzzer, etc.)',
  },
  general: {
    color: '#6C757D', // Gray
    backgroundColor: 'rgba(108, 117, 125, 0.1)',
    borderColor: '#6C757D',
    label: 'General Note',
    icon: '💬', // Speech bubble for general
    description: 'General purpose notes',
  },
};

/**
 * Helper function to get note source configuration
 */
export const getNoteSourceConfig = (source: keyof typeof noteSourceColors) => {
  return noteSourceColors[source] || noteSourceColors.order;
};

/**
 * Helper function to get note type configuration
 */
export const getNoteTypeConfig = (type: keyof typeof noteTypeColors) => {
  return noteTypeColors[type] || noteTypeColors.general;
};

export type NoteSource = keyof typeof noteSourceColors;
export type NoteType = keyof typeof noteTypeColors;

export default muiTheme;